import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Expand, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";

const FILTERS = ["Todos", "Blackwork", "Fine Line", "Tradicional", "Projetos Grandes"];
const SPANS = [
  "sm:col-span-2 sm:row-span-2", "", "", "sm:row-span-2",
  "", "sm:col-span-2", "", "", "sm:col-span-2",
];

const fadeUp = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export const Gallery = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const param = searchParams.get("estilo");
  const filter = FILTERS.includes(param) ? param : "Todos";

  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
    if (FILTERS.includes(param)) {
      setTimeout(() => document.querySelector("#galeria")?.scrollIntoView({ behavior: "smooth" }), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = (f) => {
    if (f === "Todos") setSearchParams({}, { replace: true });
    else setSearchParams({ estilo: f }, { replace: true });
  };

  const shareCategory = (e, f) => {
    e.stopPropagation();
    const url = `${window.location.origin}/?estilo=${encodeURIComponent(f)}`;
    const text = encodeURIComponent(`Confira meus trabalhos de ${f} — Dudu Tattoo: ${url}`);
    window.open(`https://api.whatsapp.com/send?text=${text}`, "_blank", "noopener");
  };

  const visible = useMemo(
    () => (filter === "Todos" ? items : items.filter((i) => i.category === filter)),
    [items, filter]
  );

  return (
    <section id="galeria" className="py-24 sm:py-32" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div {...fadeUp} className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-12">
          <div>
            <p className="font-mono-dt text-xs uppercase tracking-[0.35em] text-[#e03131] mb-4">Portfólio</p>
            <h2 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl" data-testid="gallery-title">
              Tinta que <span className="text-outline">conta história</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {FILTERS.map((f) => {
              const slug = f.toLowerCase().replace(/\s+/g, "-");
              const active = filter === f;
              return (
                <div key={f}
                  className={`flex items-center rounded-full border transition-colors duration-300 ${
                    active ? "bg-[#e03131] border-[#e03131] text-white" : "border-[#27272a] text-zinc-400 hover:border-zinc-500 hover:text-[#e2d9cc]"
                  }`}>
                  <button onClick={() => setFilter(f)} data-testid={`filter-tab-${slug}`}
                    className="pl-4 pr-3 py-2 font-mono-dt text-[11px] uppercase tracking-[0.15em]">
                    {f}
                  </button>
                  {f !== "Todos" && (
                    <button onClick={(e) => shareCategory(e, f)} aria-label={`Compartilhar ${f} no WhatsApp`}
                      data-testid={`share-whatsapp-${slug}`} title={`Enviar link de ${f} no WhatsApp`}
                      className={`pr-3.5 py-2 transition-colors duration-300 ${active ? "text-white/80 hover:text-white" : "text-zinc-600 hover:text-[#25D366]"}`}>
                      <MessageCircle size={13} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-3 auto-rows-[240px] gap-4" data-testid="gallery-grid">
          <AnimatePresence mode="popLayout">
            {visible.map((item, i) => (
              <motion.button
                layout key={item.id} data-testid={`gallery-item-${i}`}
                initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setLightbox(item)}
                className={`group relative overflow-hidden rounded-lg border border-[#27272a]/70 text-left ${SPANS[i % SPANS.length]}`}
              >
                <img src={item.url} alt={item.title} loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090b]/95 via-[#09090b]/20 to-transparent opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="absolute bottom-0 left-0 right-0 p-5 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                  <p className="font-mono-dt text-[10px] uppercase tracking-[0.25em] text-[#e03131]">{item.category}</p>
                  <h3 className="font-display font-semibold text-base sm:text-lg mt-1 text-[#e2d9cc]">{item.title}</h3>
                  <p className="text-xs text-zinc-500 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-500">por {item.artist}</p>
                </div>
                <Expand size={16} className="absolute top-4 right-4 text-[#e2d9cc] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
        {visible.length === 0 && (
          <p className="text-center text-zinc-500 font-mono-dt text-xs uppercase tracking-[0.2em] py-16" data-testid="gallery-empty">
            Nenhum trabalho nesta categoria ainda
          </p>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#09090b]/95 backdrop-blur-md flex items-center justify-center p-5"
            onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
            <motion.div initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 12 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={lightbox.url} alt={lightbox.title} className="w-full max-h-[75vh] object-contain rounded-lg border border-[#27272a]" />
              <div className="mt-4 flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono-dt text-[10px] uppercase tracking-[0.25em] text-[#e03131]">{lightbox.category}</p>
                  <h3 className="font-display font-semibold text-xl mt-1" data-testid="lightbox-title">{lightbox.title}</h3>
                  <p className="text-sm text-zinc-500 mt-1">por {lightbox.artist}</p>
                </div>
                <button onClick={() => setLightbox(null)} data-testid="lightbox-close-button" aria-label="Fechar"
                  className="p-2.5 rounded-md border border-[#27272a] text-zinc-400 hover:text-[#e03131] hover:border-[#e03131]/50 transition-colors">
                  <X size={18} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
